/**
 * Cell Allocation — the cycle page's Allocation tab.
 *
 * The portfolio question is "how much of us is working tactically versus
 * strategically", not "how much does Engineering get". A cycle's owner groups
 * comparison cells (item category × altitude) into bands and gives each band a
 * share of the cycle's committed work. Every track still ranks freely inside
 * its own cell: allocation replaces cross-cell ranking, it never ranks.
 *
 * Reads: the bands and the consumption read model (migrations 816, 972). The
 * committed work is read from the items above each track's cut line, sized by
 * item effort, because the cell lives on the item, not on the track.
 * Writes: set_cell_allocation, the one write path for the shares, and
 * record_cell_allocation_opened when the owner opens the tab (the accept-time
 * warning waits for that first look). Nothing is kept in the browser.
 *
 * Shares do not have to add up to 100 (owner ruling R-1, Oct 14): the screen
 * warns when they go over, and names the cells no band covers.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid2X2, Plus, Trash2, Loader2 } from 'lucide-react';
import { Panel, ProgressBar, EmptyState, Callout, TextInput, DataTable, type Column } from '@/components/ds';
import {
  panel, panelInset, heading, chip, status, btnPrimary, btnSecondary, btnDanger, FOCUS_RING,
  TONE_BORDER, type Tone,
} from '@/components/mono/primitives';
import { getAltitudeName, type AltitudeLevel } from '@/types/prioritization/itemLifecycle';
import {
  ALTITUDES_HIGH_TO_LOW,
  bandIndexForCell,
  cellKey,
  cellLabel,
  gridCategories,
  shareTotal,
  shareTotalNote,
  toggleCellInBand,
  uncoveredCells,
  validateBands,
  verdictLabel,
  type CellAllocationBandDraft,
  type CellRef,
  type ShareVerdict,
} from '@/lib/cellAllocation';
import {
  fetchCellAllocationBands,
  fetchCellAllocationConsumption,
  recordCellAllocationOpened,
  saveCellAllocation,
  type CellAllocationConsumption,
} from '@/lib/cellAllocationService';

const BAND_TONES: Tone[] = ['mustard', 'green', 'slate', 'coral'];
const bandTone = (index: number): Tone => BAND_TONES[index % BAND_TONES.length];

const VERDICT_TONE: Record<ShareVerdict, Tone> = {
  over: 'coral',
  under: 'mustard',
  on: 'green',
  empty: 'neutral',
};

function fmtUnits(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
function fmtPct(n: number | null): string {
  return n == null ? '—' : `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}
function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface GridRow {
  alt: AltitudeLevel;
}

interface Props {
  cycleId: string;
  /** The cycle owner or a workspace admin. Others see the bands and the numbers. */
  canEdit: boolean;
}

export function CellAllocationPanel({ cycleId, canEdit }: Props) {
  const [saved, setSaved] = useState<CellAllocationBandDraft[]>([]);
  const [draft, setDraft] = useState<CellAllocationBandDraft[]>([]);
  const [consumption, setConsumption] = useState<CellAllocationConsumption | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [bands, cons] = await Promise.all([
        fetchCellAllocationBands(cycleId),
        fetchCellAllocationConsumption(cycleId),
      ]);
      setSaved(bands);
      setDraft(bands);
      setConsumption(cons);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load the allocation.');
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => { void load(); }, [load]);

  // The owner's first look. The accept-time warning waits for it; a failure
  // here only delays that warning, so it never blocks the tab.
  useEffect(() => {
    if (!canEdit) return;
    recordCellAllocationOpened(cycleId).catch((e) => {
      console.warn('[CellAllocationPanel] could not record the first look:', e);
    });
  }, [cycleId, canEdit]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);
  const problem = useMemo(() => validateBands(draft), [draft]);
  const totalNote = shareTotalNote(draft);

  const categories = useMemo(
    () => gridCategories((consumption?.cells ?? []).map((c) => c.itemCategory)),
    [consumption],
  );
  const cellStats = useMemo(() => {
    const map = new Map<string, { committedUnits: number; committedItems: number; proposedUnits: number; proposedItems: number }>();
    for (const c of consumption?.cells ?? []) map.set(cellKey(c), c);
    return map;
  }, [consumption]);
  // Cells no band covers: the ones holding work first, named with their work.
  const uncovered = useMemo(() => {
    const all = uncoveredCells(draft, categories);
    const holding = all.filter((c) => {
      const st = cellStats.get(cellKey(c));
      return st != null && st.committedItems + st.proposedItems > 0;
    });
    return { all, holding, empty: all.filter((c) => !holding.includes(c)) };
  }, [draft, categories, cellStats]);

  function addBand() {
    setDraft((d) => [...d, { name: `Band ${d.length + 1}`, sharePercent: Math.max(0, 100 - shareTotal(d)), cells: [] }]);
    setEditingIndex(draft.length);
  }
  function removeBand(i: number) {
    setDraft((d) => d.filter((_, j) => j !== i));
    setEditingIndex(null);
  }
  function updateBand(i: number, patch: Partial<CellAllocationBandDraft>) {
    setDraft((d) => d.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  }
  function onCellPress(cell: CellRef) {
    if (editingIndex == null) return;
    setDraft((d) => toggleCellInBand(d, editingIndex, cell));
  }
  async function onSave() {
    if (problem) return;
    setSaving(true);
    setSaveError(null);
    try {
      const r = await saveCellAllocation(cycleId, draft);
      if (!r.success) {
        setSaveError(r.error ?? 'The allocation was not saved.');
        return;
      }
      setEditingIndex(null);
      await load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'The allocation was not saved.');
    } finally {
      setSaving(false);
    }
  }
  function onDiscard() {
    setDraft(saved);
    setEditingIndex(null);
    setSaveError(null);
  }

  const gridRows = useMemo<GridRow[]>(() => ALTITUDES_HIGH_TO_LOW.map((alt) => ({ alt })), []);
  const gridColumns = useMemo<Column<GridRow>[]>(() => {
    const altColumn: Column<GridRow> = {
      key: 'alt',
      header: 'Altitude',
      width: '5rem',
      render: (r) => <span className="font-mono text-[0.72rem] text-[var(--text-ter)]">{r.alt}</span>,
    };
    const cellColumns: Column<GridRow>[] = categories.map((cat) => ({
      key: cat,
      header: titleCase(cat),
      cellClassName: 'p-0 align-top',
      render: (r) => {
        const cell: CellRef = { itemCategory: cat, altitudeLevel: r.alt };
        const idx = bandIndexForCell(draft, cell);
        const stats = cellStats.get(cellKey(cell));
        const inEditing = editingIndex != null && idx === editingIndex;
        const tone = idx >= 0 ? bandTone(idx) : null;
        const body = (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-[var(--text-pri)]">{getAltitudeName(r.alt, cat)}</span>
              {idx >= 0 && <span {...chip(tone!, 'px-1.5 py-0')}>{idx + 1}</span>}
            </div>
            <div className="mt-1 font-mono text-[0.7rem] text-[var(--text-sec)]">
              {stats && stats.committedItems > 0
                ? `${fmtUnits(stats.committedUnits)} cu · ${stats.committedItems} item${stats.committedItems === 1 ? '' : 's'}`
                : '—'}
              {stats && stats.proposedItems > 0 && (
                <span className="text-[var(--text-ter)]"> · +{fmtUnits(stats.proposedUnits)} proposed</span>
              )}
            </div>
          </>
        );
        const base = 'block w-full min-w-[7.5rem] px-3 py-2 text-left align-top text-xs';
        const style: React.CSSProperties = {
          boxShadow: tone ? `inset 3px 0 0 ${TONE_BORDER[tone]}` : undefined,
          outline: inEditing ? `2px solid ${TONE_BORDER[tone ?? 'mustard']}` : undefined,
          outlineOffset: inEditing ? '-2px' : undefined,
        };
        return editingIndex != null && canEdit ? (
          <button
            type="button"
            onClick={() => onCellPress(cell)}
            aria-pressed={inEditing}
            aria-label={`${cellLabel(cell)}${inEditing ? ', in this band' : idx >= 0 ? `, in band ${idx + 1}` : ''}`}
            className={`${base} hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
            style={style}
          >
            {body}
          </button>
        ) : (
          <div className={base} style={style}>{body}</div>
        );
      },
    }));
    return [altColumn, ...cellColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, draft, cellStats, editingIndex, canEdit]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-[var(--text-ter)]">
        <Loader2 className="h-4 w-4 animate-spin" /> Reading the allocation…
      </div>
    );
  }
  if (loadError) {
    return (
      <Callout tone="coral" label="Could not load" title="The allocation did not load">
        {loadError}
      </Callout>
    );
  }

  const editing = editingIndex != null ? draft[editingIndex] : null;
  const totals = consumption?.totals;

  return (
    <div className="space-y-6" data-testid="cell-allocation">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h3 {...heading()}>Allocation</h3>
          <p className="mt-1 text-sm text-[var(--text-sec)]">
            Shares of this cycle&rsquo;s committed work by comparison cell. Group cells into bands and give each band a share;
            every track still ranks freely inside its own cell. Allocation replaces ranking across cells.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {dirty && (
              <button type="button" onClick={onDiscard} disabled={saving} {...btnSecondary()}>
                Discard
              </button>
            )}
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={!dirty || saving || problem != null}
              title={problem ?? undefined}
              {...btnPrimary()}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save shares
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <Callout tone="coral" label="Not saved" title="The database refused the shares">
          {saveError}
        </Callout>
      )}

      {/* Empty state */}
      {draft.length === 0 && (
        <EmptyState
          icon={Grid2X2}
          title="No shares set for this cycle"
          description={
            <>
              A band is a name, a share, and the cells it covers. The glossary&rsquo;s example: 20% to small problems and
              small solutions, 50% to epics and projects, 30% to strategies. Until shares are set, the grid below only
              shows what is committed in each cell.
            </>
          }
          action={canEdit ? (
            <button type="button" onClick={addBand} {...btnPrimary()}>
              <Plus className="h-3.5 w-3.5" /> Add the first band
            </button>
          ) : undefined}
        />
      )}

      {/* Bands */}
      {draft.length > 0 && (
        <Panel
          label="Bands"
          title="Shares"
          count={draft.length}
          actions={canEdit ? (
            <button type="button" onClick={addBand} {...btnSecondary()}>
              <Plus className="h-3.5 w-3.5" /> Add band
            </button>
          ) : undefined}
          flush
        >
          <ul>
            {draft.map((band, i) => {
              const tone = bandTone(i);
              const isEditing = editingIndex === i;
              return (
                <li
                  key={band.id ?? `draft-${i}`}
                  className="flex flex-wrap items-center gap-3 border-b px-4 py-3"
                  style={{ borderColor: 'var(--rule)', boxShadow: `inset 3px 0 0 ${TONE_BORDER[tone]}` }}
                >
                  <span {...chip(tone)}>{i + 1}</span>
                  {canEdit ? (
                    <TextInput
                      aria-label={`Band ${i + 1} name`}
                      value={band.name}
                      onChange={(e) => updateBand(i, { name: e.target.value })}
                      className="w-56"
                      size="sm"
                    />
                  ) : (
                    <span className="text-sm font-medium text-[var(--text-pri)]">{band.name}</span>
                  )}
                  <label className="flex items-center gap-1.5 text-sm text-[var(--text-sec)]">
                    {canEdit ? (
                      <TextInput
                        aria-label={`Band ${i + 1} share percent`}
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={Number.isFinite(band.sharePercent) ? band.sharePercent : ''}
                        onChange={(e) => updateBand(i, { sharePercent: e.target.value === '' ? NaN : Number(e.target.value) })}
                        className="w-20"
                        size="sm"
                      />
                    ) : (
                      <span className="font-mono text-sm text-[var(--text-pri)]">{fmtPct(band.sharePercent)}</span>
                    )}
                    {canEdit && <span>%</span>}
                  </label>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    {band.cells.length === 0 ? (
                      <span className="text-xs text-[var(--text-ter)]">
                        {isEditing ? 'Press cells in the grid to add them.' : 'No cells yet.'}
                      </span>
                    ) : (
                      band.cells.map((c) => (
                        <span key={cellKey(c)} {...chip('neutral', 'normal-case tracking-normal')}>
                          {cellLabel(c)}
                        </span>
                      ))
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingIndex(isEditing ? null : i)}
                        aria-pressed={isEditing}
                        {...(isEditing ? btnPrimary() : btnSecondary())}
                      >
                        {isEditing ? 'Done' : 'Choose cells'}
                      </button>
                      <button type="button" onClick={() => removeBand(i)} aria-label={`Remove band ${band.name}`} {...btnDanger('px-2.5')}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            {totalNote && <span {...status(totalNote.tone)}>{totalNote.text}</span>}
            {problem && <span {...status('coral')}>{problem}</span>}
          </div>
          {uncovered.all.length > 0 && (
            <div className="border-t px-4 py-3 text-xs text-[var(--text-sec)]" style={{ borderColor: 'var(--rule)' }} data-testid="uncovered-cells">
              <p className="font-medium text-[var(--text-pri)]">
                No band covers {uncovered.all.length} cell{uncovered.all.length === 1 ? '' : 's'}.
              </p>
              {uncovered.holding.length > 0 && (
                <p className="mt-1">
                  Holding work:{' '}
                  {uncovered.holding.map((c) => {
                    const st = cellStats.get(cellKey(c))!;
                    const parts = [
                      st.committedItems > 0 ? `${fmtUnits(st.committedUnits)} cu committed` : null,
                      st.proposedItems > 0 ? `${fmtUnits(st.proposedUnits)} cu proposed` : null,
                    ].filter(Boolean).join(', ');
                    return `${cellLabel(c)} (${parts})`;
                  }).join('; ')}.
                </p>
              )}
              {uncovered.empty.length > 0 && (
                <p className="mt-1 text-[var(--text-ter)]">
                  {uncovered.holding.length > 0 ? 'Holding nothing yet: ' : ''}
                  {uncovered.empty.map(cellLabel).join(', ')}.
                </p>
              )}
            </div>
          )}
        </Panel>
      )}

      {/* The grid */}
      <Panel
        label="Comparison cells"
        title={editing ? `Choosing cells for “${editing.name || `Band ${editingIndex! + 1}`}”` : 'Committed work by cell'}
        flush
      >
        <DataTable<GridRow>
          columns={gridColumns}
          rows={gridRows}
          getRowKey={(r) => String(r.alt)}
          bordered={false}
          density="compact"
          caption="Committed work by comparison cell: item category across, altitude down"
          captionHidden
        />
        {consumption && (
          <p className="px-4 py-2 text-xs text-[var(--text-ter)]">
            {consumption.basis}.
            {consumption.unplaced.committedItems + consumption.unplaced.proposedItems > 0 && (
              <> {consumption.unplaced.committedItems + consumption.unplaced.proposedItems} item
              {consumption.unplaced.committedItems + consumption.unplaced.proposedItems === 1 ? '' : 's'} above a cut line
              carry no cell and are not counted.</>
            )}
          </p>
        )}
      </Panel>

      {/* Planned beside above the line */}
      {consumption && consumption.bands.length > 0 && (
        <Panel label="Planned beside above the line" title="Is this what we meant?">
          <div className="space-y-5">
            {totals && (
              <p className="text-xs text-[var(--text-ter)]">
                {fmtUnits(totals.committedUnits)} capacity units committed across {totals.committedItems} item
                {totals.committedItems === 1 ? '' : 's'}
                {totals.proposedItems > 0 && `; ${fmtUnits(totals.proposedUnits)} more proposed across ${totals.proposedItems}`}.
              </p>
            )}
            {consumption.bands.map((b, i) => (
              <div key={b.id} {...panelInset('p-4')} style={{ ...panelInset().style, boxShadow: `inset 3px 0 0 ${TONE_BORDER[bandTone(i)]}` }}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-[var(--text-pri)]">{b.name}</span>
                  <span {...status(VERDICT_TONE[b.verdict])}>{verdictLabel(b.verdict, b.gapPoints)}</span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <ProgressBar label="Planned share" value={b.sharePercent} max={100} tone="slate" showValue format={(v) => fmtPct(v)} size="sm" />
                  <ProgressBar
                    label="Above-the-line share"
                    value={b.committedShare ?? 0}
                    max={100}
                    tone={VERDICT_TONE[b.verdict]}
                    showValue
                    format={() => fmtPct(b.committedShare)}
                    size="sm"
                  />
                </div>
                <p className="mt-2 font-mono text-[0.7rem] text-[var(--text-ter)]">
                  {fmtUnits(b.committedUnits)} cu across {b.committedItems} item{b.committedItems === 1 ? '' : 's'}
                  {b.proposedItems > 0 && b.projectedShare != null && ` · with proposals: ${fmtPct(b.projectedShare)}`}
                </p>
              </div>
            ))}
            {consumption.unallocated.cells.length > 0 && (
              <Callout tone="mustard" label="Not in any band" title="Cells with committed work that no band covers">
                <span className="text-sm">
                  {consumption.unallocated.cells.map((c) => `${cellLabel(c)} (${fmtUnits(c.committedUnits)} cu)`).join(', ')}
                  {consumption.unallocated.committedShare != null && ` · ${fmtPct(consumption.unallocated.committedShare)} of the committed work`}
                </span>
              </Callout>
            )}
          </div>
        </Panel>
      )}

      {/* Nothing committed yet, bands exist */}
      {consumption && consumption.bands.length > 0 && totals && totals.committedItems === 0 && totals.proposedItems === 0 && (
        <div {...panel('p-4')}>
          <span {...status('neutral')}>Nothing is above a cut line yet. The shares will be compared once a track&rsquo;s list is decided.</span>
        </div>
      )}
    </div>
  );
}

export default CellAllocationPanel;
